import axios, { AxiosError, AxiosHeaderValue, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import CryptoES from 'crypto-es';
import { jwtDecode } from 'jwt-decode';
import environment from './environment';
import { ReducersTypes } from '../redux/reducers/Models';
import { Store } from '@reduxjs/toolkit';
import { setUser } from '@/redux/ducks/user';
import { clearCustomer, setCustomer } from '@/redux/ducks/customer';
import packageJson from '../../package.json';

const headers: {
  [key: string]: AxiosHeaderValue;
} = {
  'Content-Type': 'application/json',
  'x-api-key': environment.api.apiKeyAuth,
  'empresa': environment.api.enterprise,
  'codigocanal': environment.api?.automaticChannel ?? '11',
  'appversion': packageJson.version,
  'cache-control': 'no-store',
  'app': environment.api?.app ?? '',
}

export const uriRefreshToken: string = `/login-app/${environment.api?.loginVersion ?? "v4"}/oauth/token`;

export const uriPrefixException = [
  "internalwebapi"
]

const api = axios.create({
  baseURL: environment.api.baseUri,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
    'codigocanal': environment.api?.channel,
  }
});

const automaticApi = axios.create({
  baseURL: environment.api.baseUriAuth,
  timeout: 30000,
  headers,
});

export const getUUID: () => Promise<string> = async () => {
  const deviceId = localStorage.getItem("device_id") || (() => {
    const newId = crypto.randomUUID();
    localStorage.setItem("device_id", newId);
    return newId;
  })();

  return deviceId;
};

export const setupAxiosInterceptors = (store: Store<ReducersTypes>) => {
  const { dispatch } = store;
  api.interceptors.request.use(
    async (config) => {
      try {

        config.headers.uuid = await getUUID();

        const position = await getUserPosition();

        config.headers.devicename = await getFuncDevicesName();
        config.headers.lat = position?.latitude ?? 0;
        config.headers.long = position?.longitude ?? 0;

        config.headers.hash = await getGenerateHash({
          payload: config.data ? Object.values(config.data).join('') : '',
          hasDate: !!hasDateInHash?.find(item => config?.url?.includes(item)),
          hashKey: hashKey.find(item => config?.url?.includes(item.uri))?.hashKey,
          position,
          enterprise: config.headers?.empresa,
        });

        config.headers['x-cpy-pos'] = getCpyPos(position);

        const user = store.getState().userReducer;

        if (!user?.apiKey) {
          try {
            const response = await axios.get(`${config.baseURL}/v1/customers/parameters`, {
              params: {
                companyIdentification: environment.api.companyIdentification,
                channelCode: environment.api.channel,
              }
            });
            const apiKey = response?.data?.data?.frontApiKey ?? "";
            dispatch(setUser({ apiKey }));
            config.headers['x-api-key'] = apiKey;
          } catch (error) { }
        } else {
          config.headers['x-api-key'] = user?.apiKey;
        }

        if (user?.accessToken) {
          config.headers.accesstoken = String(user.accessToken);
        }

        if (user?.accessTokenTickets) {
          config.headers.accesstoken = String(user.accessTokenTickets);
        }

      } catch (error) { }

      return config;
    }
  );
  api.interceptors.response.use(
    response => response,
    (error) => {
      if (error?.response?.status === 401) {
        window.location.href = '/';
      }
      return Promise.reject(error);
    }
  );

  const expiredSession = () => {
    const customer = store.getState().customerReducer;
    dispatch(
      clearCustomer()
    );
    dispatch(
      setCustomer({ username: customer?.username })
    );
    window.location.href = '/';
  }

  const canceRequest: () => void = () => {
    throw new axios.Cancel('Operation canceled because token expired.');
  };

  const refreshTokenGlobal: (
    refreshToken: string,
  ) => Promise<void> = async refreshToken => {
    await automaticApi
      .post(
        uriRefreshToken,
        {},
        {
          headers: {
            refreshtoken: refreshToken,
          },
        },
      )
      .then(async (resp: AxiosResponse) => {
        if (resp?.data?.data?.accessToken) {
          dispatch(
            setCustomer({ accessToken: resp?.data?.data?.accessToken }),
          );
        } else {
          expiredSession();
          canceRequest();
        }
      })
      .catch((_: AxiosError) => {
        expiredSession();
        canceRequest();
      });
  };

  const forcedRefresh: (
    config: InternalAxiosRequestConfig,
  ) => Promise<InternalAxiosRequestConfig> = async (config: InternalAxiosRequestConfig) => {

    const customer = store.getState().customerReducer;

    const accessToken = customer?.accessToken;
    const refreshToken = customer?.refreshToken;

    const prefix = environment?.api?.prefix ?? '';

    let formattedUri;
    if (prefix !== "" && !uriRefreshToken.includes(prefix)) {
      formattedUri = `/${prefix}${uriRefreshToken.substring(1)}`;
    } else {
      formattedUri = uriRefreshToken;
    }

    if (accessToken && config.url !== formattedUri) {
      const {
        exp,
      }: {
        exp: number;
      } = jwtDecode(accessToken);
      if (exp) {
        const date = new Date(exp * 1000);
        const now = new Date();
        const diff = date.getTime() - now.getTime();
        const minutesDiff = diff / (1000 * 60);
        if (minutesDiff < 3) {
          await refreshTokenGlobal(refreshToken!);
        }
      }

      const accessTokenUpdate = store.getState().customerReducer?.accessToken;
      config.headers.accesstoken = `${accessTokenUpdate}`;
    }

    if (accessToken) {
      const {
        NRC,
      }: {
        NRC: string;
      } = jwtDecode(accessToken);
      config.headers.codigocliente = `${NRC}`;
    }

    return config;
  };

  const getCpyPos = (position: {
    latitude: number;
    longitude: number;
    accuracy: number;
    altitude: number | null;
    heading: number | null;
    speed: number | null;
    altitudeAccuracy?: number | null;
  } | null) => {
    try {
      if (!global?.Buffer) {
        global.Buffer = require('buffer').Buffer;
      }
      const formattedPosition = JSON.stringify(position ?? {});
      return Buffer.from(formattedPosition, 'utf8').toString("base64");
    } catch (error) {
      return '';
    }
  }

  const getGenerateHash: (data: {
    payload: string;
    hasDate?: boolean;
    hashKey?: string;
    position: {
      latitude: number;
      longitude: number;
      accuracy: number;
      altitude: number | null;
      heading: number | null;
      speed: number | null;
      altitudeAccuracy?: number | null;
    } | null;
    enterprise: string;
  }) => Promise<string> = async (data) => {
    const deviceName: string = await getFuncDevicesName();
    const uuid = await getUUID();

    let date = new Date().toISOString();
    return CryptoES.HmacSHA512(
      data.payload +
      uuid +
      deviceName +
      data.enterprise +
      (data?.position?.latitude ?? 0) +
      (data?.hasDate ? date.substring(0, 11) : ''),
      data?.hashKey ?? environment.api.hashKey).toString();
  }

  const hasDateInHash: string[] = [
    `/login-app/${environment.api?.loginVersion ?? "v4"}/authorize`
  ];

  const hashKey: { uri: string; hashKey: string }[] = [
    {
      uri: hasDateInHash[0],
      hashKey: environment.api.hashKey,
    }
  ];

  function getUserPosition(): Promise<{
    latitude: number;
    longitude: number;
    accuracy: number;
    altitude: number | null;
    heading: number | null;
    speed: number | null;
    altitudeAccuracy?: number | null;
  } | null> {
    return new Promise((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(
        ({ coords }) => {
          resolve(coords);
        },
        _error => {
          resolve(null);
        },
        {
          enableHighAccuracy: true,
          timeout: 1500,
          maximumAge: 20000,
        },
      );
    });
  }

  async function getFuncDevicesName() {
    return encodeURIComponent(navigator.userAgent.replace(/\'/g, ''));
  }

  const getRequestInterceptor = async (config: InternalAxiosRequestConfig & {
    headers: {
      uuid: string;
      devicename: string;
      lat: number;
      long: number;
    }
  }): Promise<InternalAxiosRequestConfig> => {
    config.headers.uuid = await getUUID();

    const position = await getUserPosition();

    config.headers.devicename = await getFuncDevicesName();
    config.headers.lat = position?.latitude ?? 0;
    config.headers.long = position?.longitude ?? 0;

    config.headers.hash = await getGenerateHash({
      payload: config.data ? Object.values(config.data).join('') : '',
      hasDate: !!hasDateInHash?.find(item => config?.url?.includes(item)),
      hashKey: hashKey.find(item => config?.url?.includes(item.uri))?.hashKey,
      position,
      enterprise: config.headers?.empresa,
    });

    config.headers['x-cpy-pos'] = getCpyPos(position);

    const prefix = environment?.api?.prefix ?? '';

    if (prefix !== "" && !config.url?.includes(prefix) && !uriPrefixException.some(item => config?.url?.includes(item))) {
      config.url = `/${prefix}${config.url?.substring(1)}`;
    }

    return await forcedRefresh(config)
  }

  automaticApi.interceptors.request.use(
    async (config) => {
      try {
        config = await getRequestInterceptor({ ...config } as InternalAxiosRequestConfig & {
          headers: {
            uuid: string;
            devicename: string;
            lat: number;
            long: number;
          };
        });
      } catch (error) { }

      return config;
    }
  );
  automaticApi.interceptors.response.use(
    response => response,
    (error) => {
      if (error?.response?.status === 401) {
        expiredSession();
      }
      return Promise.reject(error);
    }
  );
};

export const getApiConfig = (isLogged: boolean) => {
  const apiInstance = isLogged ? automaticApi : api;
  const prefixUri = isLogged ? 'internalwebapi' : '';
  return { apiInstance, prefixUri };
};

export { automaticApi };
export default api;